import React, { useEffect, useMemo } from 'react';
import {
  DefaultValues,
  FieldPath,
  FieldPathValue,
  FieldValues,
  Path,
  PathValue,
  useForm,
  UseFormReturn,
  WatchObserver,
} from 'react-hook-form';

import { getDirtyFields } from 'utils/calculateDirtyFields';
import { getValueFromObject } from 'utils/valueFromObject';

import { FormProps } from '../typing';
import {
  buildBaseFormEnvironment,
  FormEnvironmentRaw,
} from './formEnvironment';

const debounce = <T extends (...args: any[]) => void>(
  callback: T,
  wait: number
) => {
  let timeoutId: number | undefined = undefined;
  return (...args: Parameters<T>) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
};

function calculatePatch<FieldsTypes extends FieldValues>(
  methods: UseFormReturn<FieldsTypes>,
  data: any
): any {
  let currentDirtyFields: object = methods.formState.dirtyFields;
  if (!currentDirtyFields || Object.keys(currentDirtyFields).length === 0) {
    currentDirtyFields = getDirtyFields(data, methods.formState.defaultValues);
  }
  if (!currentDirtyFields || Object.keys(currentDirtyFields).length === 0)
    return undefined;

  return Object.entries(currentDirtyFields)
    .filter(([_, value]) => value !== false)
    .reduce((accumulator, [key]) => {
      accumulator[key] = data[key];
      return accumulator;
    }, {} as any);
}

function buildFormEnvironmentRaw<FieldsTypes extends FieldValues>(
  methods: UseFormReturn<FieldsTypes>
): FormEnvironmentRaw<FieldsTypes> {
  return {
    submitFactory(
      onValid: (data: FieldsTypes, event?: React.BaseSyntheticEvent) => unknown
    ): (e?: React.BaseSyntheticEvent) => Promise<void> {
      return methods.handleSubmit(onValid);
    },
    setFocus(path: string) {
      return methods.setFocus(path as FieldPath<FieldsTypes>);
    },
    watchValue<T = unknown>(value: string, defaultValue?: T): T | undefined {
      return methods.watch(
        value as Path<FieldsTypes>,
        defaultValue as PathValue<FieldsTypes, Path<FieldsTypes>> | undefined
      );
    },
    getDefaultValues(): object | undefined {
      return methods.formState.defaultValues;
    },
    triggerFormValidation(paths): Promise<boolean> {
      if (!paths || paths.length === 0) return methods.trigger();

      return methods.trigger(paths as Path<FieldsTypes>[]);
    },
    watch: methods.watch,
    getDefaultValue<T = string>(path: string): T | undefined {
      return getValueFromObject<T>(methods.formState.defaultValues, path);
    },
    getValue<T = string>(path?: string): T | undefined {
      return methods.getValues(path as FieldPath<FieldsTypes>);
    },
    getFormState(): {
      isDirty: boolean;
      dirtyFields: object;
      isSubmitting: boolean;
      isValid: boolean;
      errors: object;
      isSubmitSuccessful: boolean;
    } {
      return methods.formState;
    },
    getPatchObject(): any {
      const updatedValues = methods.getValues();
      return calculatePatch(methods, updatedValues);
    },
    getPatchObjectWithFormData(data: any): any {
      return calculatePatch(methods, data);
    },
    setValue(
      path: string,
      value: unknown,
      opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
    ) {
      methods.setValue(
        path as FieldPath<FieldsTypes>,
        value as FieldPathValue<FieldsTypes, FieldPath<FieldsTypes>>,
        {
          shouldValidate: opts ? opts.shouldValidate : true,
          shouldDirty: opts ? opts.shouldDirty : true,
        }
      );
    },
    setError(path: string, error: { type: string; message: string }) {
      methods.setError(path as FieldPath<FieldsTypes>, {
        type: error.type,
        message: error.message,
      });
    },
    resetField(path: string, defaultValue: any) {
      methods.resetField(path as FieldPath<FieldsTypes>, {
        defaultValue,
        keepError: false,
      });
    },
    clearErrors(path?: string[] | string) {
      methods.clearErrors(path as FieldPath<FieldsTypes>);
    },
    reset(values?: FieldsTypes, options?: Record<string, boolean>): void {
      methods.reset(values, options);
    },
  };
}

const useFormLogic = <FieldsTypes extends FieldValues>({
  defaultValues,
  onValidChange,
  onChange,
  validateDefaultValues,
  readonly,
  shouldUnregister,
}: FormProps<FieldsTypes>) => {
  // region *******************************DATA*********************************
  const trimmedDefaultValues = useMemo(() => {
    if (!defaultValues) return undefined;

    const keys: DefaultValues<FieldsTypes> = {} as DefaultValues<FieldsTypes>;

    for (const [key, value] of Object.entries(defaultValues)) {
      if (typeof value === 'string') {
        keys[key] = value.trim();
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        keys[key] = value;
      }
    }

    return keys;
  }, [defaultValues]);

  const methods = useForm<FieldsTypes>({
    defaultValues: trimmedDefaultValues,
    mode: 'onBlur',
    disabled: readonly,
    shouldUnregister,
  });

  const providerValue = useMemo<FormEnvironmentRaw<FieldsTypes>>(
    () => buildFormEnvironmentRaw<FieldsTypes>(methods),
    [methods]
  );

  const formEnvironment = useMemo(
    () => buildBaseFormEnvironment<FieldsTypes>(providerValue),
    [providerValue]
  );

  // endregion

  const {
    watch,
    formState: { isValid, errors },
    trigger,
  } = methods;

  // region EFFECTS
  useEffect(() => {
    onValidChange?.(isValid, errors);
  }, [errors, isValid, onValidChange]);

  useEffect(() => {
    if (!onChange) return;

    const subscription = watch(
      debounce(onChange, 200) as WatchObserver<FieldsTypes>
    ); // we have to cast this back for the same reason as we constructed a replacement type above
    return () => subscription.unsubscribe();
  }, [onChange, watch]);

  useEffect(() => {
    if (!validateDefaultValues) return;

    void trigger().then();
  }, [trigger, validateDefaultValues]);

  // endregion

  return useMemo(
    () => ({ methods, providerValue, formEnvironment }),
    [formEnvironment, methods, providerValue]
  );
};

export default useFormLogic;
